// Drag-handle wrapper for touch-based sorting.
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useT } from '@/i18n/useT';

/**
 * Sortable wrapper for mobile task items. Registers the task with dnd-kit
 * and renders a drag handle. The actual task content is passed as children.
 *
 * @param {object} props
 * @param {import('@/types').Task} props.task
 * @param {'daily-task'|'next-action'} props.type - The dnd-kit data type for this item.
 * @param {React.ReactNode} props.children
 */
const TaskItemMobileSortable = ({ task, type = 'daily-task', children }) => {
  const t = useT();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
  } = useSortable({
    id: task.id,
    data: { type, task },
  });

  // When dragging, the DragOverlay follows the cursor — the original item
  // becomes a ghost slot that only reserves space. Make it nearly invisible
  // so it doesn't appear to "jump around" as dnd-kit repositions it.
  // Only dynamic values that cannot live in CSS are kept as inline style.
  const DZINDEX_DRAG_OVERLAY = 10;
  const dynamicStyle = {
    // Fallback to translate3d(0,0,0) instead of letting CSS.Transform.toString
    // return undefined. A missing transform property causes Firefox to exit
    // GPU-compositing mode mid-transition, producing an invisible "ghost layer".
    transform: transform
      ? CSS.Transform.toString(transform)
      : 'translate3d(0, 0, 0)',
    transition:
      [transition, 'opacity 120ms ease'].filter(Boolean).join(', ') || 'none',
    zIndex: isDragging ? DZINDEX_DRAG_OVERLAY : 1,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        ...(isOver && {
          borderRadius: 1.5,
          backgroundColor: 'var(--color-primary-200)',
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: -2,
        }),
      }}
      style={dynamicStyle}
    >
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
      <Box
        {...attributes}
        {...listeners}
        component="div"
        data-testid="drag-handle"
        aria-label={t(
          'taskitemmobilesortable.dragToReorder',
          'Drag to reorder',
        )}
        sx={{
          touchAction: 'none',
          py: 2,
          px: 1.5,
          minWidth: 44,
          minHeight: 44,
          color: 'action.disabled',
          opacity: 0.45,
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
    </Box>
  );
};

export default TaskItemMobileSortable;
